import {
  Link as MantineLink,
  RichTextEditor as MantineRichTextEditor,
  useRichTextEditorContext as useMantineRichTextEditorContext,
} from "@mantine/tiptap";
import type { Editor } from "@tiptap/react";
import * as React from "react";
import type { ElementProps, StyleProps } from "../primitives/style-props";

/** The editor shell used by ShelfCycle's existing rich-text surface. */
export interface RichTextEditorProps extends ElementProps<"div">, StyleProps {
  editor: Editor | null;
  children: React.ReactNode;
  withCodeHighlightStyles?: boolean;
  withTypographyStyles?: boolean;
}

export interface RichTextEditorToolbarProps extends ElementProps<"div">, StyleProps {
  children?: React.ReactNode;
  sticky?: boolean;
  stickyOffset?: React.CSSProperties["top"];
}

export interface RichTextEditorContentProps extends ElementProps<"div">, StyleProps {}
export interface RichTextEditorControlsGroupProps extends ElementProps<"div">, StyleProps {}
export interface RichTextEditorControlProps extends ElementProps<"button">, StyleProps {
  active?: boolean;
  interactive?: boolean;
}

/** Only the editor value required by external controls is public. */
export interface RichTextEditorContext {
  editor: Editor;
}

/**
 * The editor's compound controls are vendor implementation details. These private casts preserve
 * the exact component identities while exposing only the attributes current callers use; the
 * rich-text-editor casts test pins every static retained below.
 */
const RichTextEditorRoot = MantineRichTextEditor as React.ComponentType<RichTextEditorProps & { readonly ref?: React.Ref<HTMLDivElement> }>;
const Toolbar = MantineRichTextEditor.Toolbar as React.ComponentType<RichTextEditorToolbarProps>;
const ControlsGroup = MantineRichTextEditor.ControlsGroup as React.ComponentType<RichTextEditorControlsGroupProps>;
const Content = MantineRichTextEditor.Content as React.ComponentType<RichTextEditorContentProps>;
const Control = MantineRichTextEditor.Control as React.ComponentType<
  RichTextEditorControlProps & { readonly ref?: React.Ref<HTMLButtonElement> }
>;
const Bold = MantineRichTextEditor.Bold as React.ComponentType<RichTextEditorControlProps>;
const Italic = MantineRichTextEditor.Italic as React.ComponentType<RichTextEditorControlProps>;
const Underline = MantineRichTextEditor.Underline as React.ComponentType<RichTextEditorControlProps>;
const Strikethrough = MantineRichTextEditor.Strikethrough as React.ComponentType<RichTextEditorControlProps>;
const ClearFormatting = MantineRichTextEditor.ClearFormatting as React.ComponentType<RichTextEditorControlProps>;
const Highlight = MantineRichTextEditor.Highlight as React.ComponentType<RichTextEditorControlProps>;
const Code = MantineRichTextEditor.Code as React.ComponentType<RichTextEditorControlProps>;
const H1 = MantineRichTextEditor.H1 as React.ComponentType<RichTextEditorControlProps>;
const H2 = MantineRichTextEditor.H2 as React.ComponentType<RichTextEditorControlProps>;
const H3 = MantineRichTextEditor.H3 as React.ComponentType<RichTextEditorControlProps>;
const H4 = MantineRichTextEditor.H4 as React.ComponentType<RichTextEditorControlProps>;
const Blockquote = MantineRichTextEditor.Blockquote as React.ComponentType<RichTextEditorControlProps>;
const Hr = MantineRichTextEditor.Hr as React.ComponentType<RichTextEditorControlProps>;
const BulletList = MantineRichTextEditor.BulletList as React.ComponentType<RichTextEditorControlProps>;
const OrderedList = MantineRichTextEditor.OrderedList as React.ComponentType<RichTextEditorControlProps>;
const Subscript = MantineRichTextEditor.Subscript as React.ComponentType<RichTextEditorControlProps>;
const Superscript = MantineRichTextEditor.Superscript as React.ComponentType<RichTextEditorControlProps>;
const LinkControl = MantineRichTextEditor.Link as React.ComponentType<RichTextEditorControlProps>;
const Unlink = MantineRichTextEditor.Unlink as React.ComponentType<RichTextEditorControlProps>;
const AlignLeft = MantineRichTextEditor.AlignLeft as React.ComponentType<RichTextEditorControlProps>;
const AlignCenter = MantineRichTextEditor.AlignCenter as React.ComponentType<RichTextEditorControlProps>;
const AlignJustify = MantineRichTextEditor.AlignJustify as React.ComponentType<RichTextEditorControlProps>;
const AlignRight = MantineRichTextEditor.AlignRight as React.ComponentType<RichTextEditorControlProps>;

const RichTextEditorBase = React.forwardRef<HTMLDivElement, RichTextEditorProps>(function RichTextEditor({ children, ...props }, ref) {
  return (
    <RichTextEditorRoot {...props} ref={ref}>
      {children}
    </RichTextEditorRoot>
  );
});

export const RichTextEditor = Object.assign(RichTextEditorBase, {
  Toolbar,
  ControlsGroup,
  Content,
  Control,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  ClearFormatting,
  Highlight,
  Code,
  H1,
  H2,
  H3,
  H4,
  Blockquote,
  Hr,
  BulletList,
  OrderedList,
  Subscript,
  Superscript,
  Link: LinkControl,
  Unlink,
  AlignLeft,
  AlignCenter,
  AlignJustify,
  AlignRight,
});

/** Tiptap's extension object is behaviorally unchanged; only its import path is now owned. */
export const Link = MantineLink;

/**
 * Hides Mantine's styling context; external controls use only the active Tiptap editor.
 *
 * Mantine 7 types the context editor as `Editor | null` — it is the root's own `editor` prop — where
 * Mantine 6 typed it `Editor`. The owned contract stays `Editor` (SC-2703): the only root render site
 * mounts the root once its editor exists, so a null here is a mounting bug, and it fails loudly
 * instead of reaching a control that assumes an editor.
 */
export const useRichTextEditorContext = (): RichTextEditorContext => {
  const { editor } = useMantineRichTextEditorContext();
  if (!editor) {
    throw new Error("useRichTextEditorContext: RichTextEditor was rendered without an editor");
  }
  return { editor };
};
